import { AuthContext } from "@/context/auth-context";
import { BBUser } from "@/types";
import { useState, useEffect, useContext, useCallback } from "react";
import { createHash } from "@/app/utils/crypto";
import { supabaseAnonClient } from "@/pages/api/utils/supabase/browser";

export type ProjectDocument = {
  id: string;
  url: string;
  name: string;
  created_at: Date;
  tags: string[];
}

const DOC_BUCKET_MAPPING = {
  "media": "media",
  "brochure": "documents",
  "raw-floorplans": "floorplans"
}

const useProjectDocs = (projectId: string, documentName?: string) => {
  const [docs, setDocs] = useState<ProjectDocument[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  const { isAdmin, isLoading } = useContext(AuthContext);
  
  const fetchData = useCallback(async () => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const baseUrl = isAdmin ? `/api/admin/project/${projectId}/document` : `/api/project/${projectId}/document`;
      const query = documentName ? `documentName=${documentName}` : undefined;
      const url = query ? `${baseUrl}?${query}` : baseUrl;
      const response = await fetch(url);
      if(response.ok) {
        const data = await response.json();
        setDocs(data.documents);
        setTotal(data.total);
      } else {
        setError('project not found');
      }
    } catch (error) {
      setError("Error fetching docs");
    } finally {
      setLoading(false);
    }
  }, [projectId, documentName, isAdmin]);

  const uploadDocument = async (
    fileContent: ArrayBuffer,
    fileName: string,
    docType: "media" | "brochure" | 'raw-floorplans'= "brochure"
  ) => {
    setUploading(true);
    try {
      const bucketName = DOC_BUCKET_MAPPING[docType];
      const hash = await createHash(fileContent);
      // always upload directly to the bucket, because vercel functions have a max upload size of 5mb
      const response = await supabaseAnonClient.storage
        .from(bucketName)
        .upload(`${docType}-${hash}`, fileContent, {
          upsert: false,
          contentType: docType === "brochure" ? "application/pdf" : "image/jpeg",
        });
      if (response.error) {
        console.log(response.error);
        if (response.error.message == "The resource already exists") {
          // file exists
        }
        throw new Error(`Failed to upload ${docType}`);
      }
      await fetch(`/api/admin/project/${projectId}/document`, {
        method: "POST",
        body: JSON.stringify({
          bucket: bucketName,
          path: response.data.path,
          docType,
          hash,
        }),
      });
      
      // Refresh document list after successful upload
      await fetchData();
      return true;
    } catch (error) {
      console.error(`Error uploading ${docType}:`, error);
      return false;
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if(isLoading) {
      return;
    }
    
    fetchData();
  }, [fetchData, isLoading]);

  return { 
    docs, 
    loading, 
    error, 
    total, 
    uploading,
    uploadDocument,
    refetch: fetchData 
  };
};

export default useProjectDocs;